import { CarrierLogo } from "@/components/carriers/CarrierLogo";
import { WorkflowRail } from "@/components/tms/WorkflowRail";
import { DataTable } from "@/components/table/DataTable";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { apiClient } from "@/lib/api/client";
import type { QuoteMode } from "@/lib/tms/recent";
import { findRecentQuote, readRecentQuotes, saveRecentQuote } from "@/lib/tms/recent";
import { formatCurrency } from "@/lib/tms/format";
import type { RateQuoteResponse } from "@/lib/api/types";
import { Spinner } from "@/components/ui/spinner";
import { ArrowRight } from "lucide-react";
import * as React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

const QUOTE_SELECTION_KEY = "shippeek-selected-quote";
const NO_REFERENCE = "__none__";

export default function QuoteRetrievePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const queryRef = searchParams.get("ref") ?? "";
  const queryMode = searchParams.get("mode") === "parcel" ? "parcel" : "ltl";

  const [refId, setRefId] = React.useState(queryRef);
  const [mode, setMode] = React.useState<QuoteMode>(queryMode);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [quote, setQuote] = React.useState<RateQuoteResponse | null>(null);
  const [recentRefs, setRecentRefs] = React.useState(() => readRecentQuotes());

  const refreshRecentRefs = React.useCallback(() => {
    setRecentRefs(readRecentQuotes());
  }, []);

  const onRetrieve = React.useCallback(
    async (event?: React.FormEvent<HTMLFormElement>) => {
      event?.preventDefault();
      const normalizedRef = refId.trim();
      if (!normalizedRef) return;
      setLoading(true);
      setError(null);

      try {
        const data = await apiClient.retrieveQuote(normalizedRef);
        setQuote(data);
        saveRecentQuote({
          mode,
          response: data,
          overrideRefId: normalizedRef,
        });
        refreshRecentRefs();
      } catch (retrieveError) {
        setError(retrieveError instanceof Error ? retrieveError.message : "Unable to retrieve quote.");
        setQuote(null);
      } finally {
        setLoading(false);
      }
    },
    [mode, refId, refreshRecentRefs],
  );

  React.useEffect(() => {
    if (!queryRef) return;
    void onRetrieve();
    // We only want one auto-retrieve on first load with query params.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toBooking = React.useCallback(
    (rateId: string) => {
      const currentRef = quote?.refId ?? quote?.id ?? refId.trim();
      if (!currentRef) return;
      localStorage.setItem(
        QUOTE_SELECTION_KEY,
        JSON.stringify({
          mode,
          refId: currentRef,
          rateId,
        }),
      );
      navigate("/shipments/book");
    },
    [mode, navigate, quote?.id, quote?.refId, refId],
  );

  return (
    <div className="space-y-6">
      <WorkflowRail current="retrieve" />

      <Card>
        <CardHeader>
          <CardTitle>Retrieve Quote</CardTitle>
          <CardDescription>Open a previous reference and continue booking without restarting lane details.</CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-6" onSubmit={(event) => void onRetrieve(event)}>
            <div className="space-y-3">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground font-mono">Quote Lookup</p>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div className="space-y-1.5">
                  <Label>Recent References</Label>
                  <Select
                    value={NO_REFERENCE}
                    onValueChange={(value) => {
                      if (value === NO_REFERENCE) return;
                      setRefId(value);
                      const match = findRecentQuote(value);
                      if (match) setMode(match.mode);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Pick from real usage history" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NO_REFERENCE}>Select reference</SelectItem>
                      {recentRefs.map((entry) => (
                        <SelectItem key={entry.refId} value={entry.refId}>
                          {entry.refId} · {entry.mode.toUpperCase()} · {entry.rates.length} rates
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="refId">Reference ID</Label>
                  <Input id="refId" value={refId} onChange={(event) => setRefId(event.target.value)} required />
                </div>
                <div className="space-y-1.5">
                  <Label>Mode</Label>
                  <Select value={mode} onValueChange={(value: QuoteMode) => setMode(value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ltl">LTL</SelectItem>
                      <SelectItem value="parcel">Parcel</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button type="submit" disabled={loading || !refId.trim()}>
                {loading ? (
                  <>
                    <Spinner size="sm" className="text-primary-foreground" />
                    Retrieving...
                  </>
                ) : (
                  "Retrieve"
                )}
              </Button>
              <Button type="button" variant="outline" onClick={() => navigate("/quotes")}>
                New Quote
              </Button>
            </div>
          </form>
          {error ? <p className="mt-3 text-sm text-destructive">{error}</p> : null}
        </CardContent>
      </Card>

      {quote ? (
        <Card>
          <CardHeader>
            <CardTitle>Retrieved Rates</CardTitle>
            <CardDescription>Reference: {quote.refId ?? quote.id}</CardDescription>
          </CardHeader>
          <CardContent>
            <DataTable
              rows={quote.rates ?? []}
              getRowId={(row, index) => `${row.ref}-${row.carrierCode}-${row.serviceType}-${index}`}
              columns={[
                {
                  key: "carrier",
                  label: "Carrier",
                  sortable: true,
                  render: (row) => (
                    <div className="flex items-center gap-2">
                      <CarrierLogo code={row.carrierCode} name={row.carrier} className="size-8 rounded-md" />
                      <span>{row.carrier}</span>
                    </div>
                  ),
                  getSortValue: (row) => row.carrier,
                  filter: { type: "select" },
                },
                {
                  key: "service",
                  label: "Service",
                  sortable: true,
                  render: (row) => row.serviceDescription || row.serviceType,
                  getSortValue: (row) => row.serviceDescription || row.serviceType,
                  filter: { type: "text", placeholder: "Service" },
                },
                {
                  key: "days",
                  label: "Transit",
                  sortable: true,
                  render: (row) => row.days ?? "-",
                  getSortValue: (row) => {
                    if (row.days === null) return Number.POSITIVE_INFINITY;
                    const numericDays = typeof row.days === "number" ? row.days : Number(row.days);
                    return Number.isFinite(numericDays) ? numericDays : Number.POSITIVE_INFINITY;
                  },
                },
                {
                  key: "total",
                  label: "Total",
                  sortable: true,
                  render: (row) => formatCurrency(row.total),
                  getSortValue: (row) => {
                    const numericTotal = typeof row.total === "number" ? row.total : Number(row.total);
                    return Number.isFinite(numericTotal) ? numericTotal : Number.POSITIVE_INFINITY;
                  },
                },
                {
                  key: "next",
                  label: "Next",
                  render: (row) => (
                    <Button type="button" size="sm" variant="outline" onClick={() => toBooking(row.ref)}>
                      Book
                      <ArrowRight className="size-4" />
                    </Button>
                  ),
                },
              ]}
            />
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
